import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { artProxyUrl } from '../artwork-texture.js';
import { studio, encoder, smooth01, MATCAP_DARK, MATCAP_GAIN } from './lib/matcap.js';
import { vdjColor } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Les dominos ont leur propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'domino',
  // the brief says 'objects'; the orchestrator's family is 'object'
  family: 'object',
  danceability: 0.64,
  requiresTier: 0,
  energyLevel: 0.55,
  bpmMin: 85,
  bpmMax: 130,
  movement: 0.60,
  tonality: 0.70,
  enabled: true
};

// DOMINO : des milliers de dominos debout sur un sol noir, vus en plongée.
// Une chaîne de chutes les couche en rythme ; couchés, leurs faces révèlent
// la pochette en mosaïque ; puis ils se relèvent et la chaîne repart. Pop,
// funk, house, hip-hop.
//
// LA GÉOMÉTRIE, et pourquoi aucun domino n'en traverse un autre : tous
// tombent VERS LA CAMÉRA (-z), en pivotant sur l'arête avant de leur base.
// Debout, chacun se tient au FOND de sa case (marge m), si bien que son
// balayage complet - le coin le plus éloigné du pivot décrit un quart de
// cercle de rayon sqrt(h² + d²) - reste dans sa propre case, et couché il en
// couvre l'avant. La face qui finit en haut est donc sa face ARRIÈRE : c'est
// elle qui porte la couleur, invisible tant qu'il est debout. Le parcours du
// front ne règle que l'ORDRE des chutes (le brief : t_i = distance le long
// de la courbe / vitesse) ; une direction de chute par parcours aurait fait
// se croiser des dominos voisins aux virages du front (vérifié dans le test).
//   case p = 1, largeur w = 0,86, hauteur h = 0,72, épaisseur d = 0,18,
//   marge 0,04 : pivot à z = 0,28, coin balayé jusqu'à z = -0,462 > -0,5.
//
// LE RENDU : un seul InstancedBufferGeometry de 24 sommets par domino (une
// boîte), les arêtes ARRONDIES par la normale analytique d'une boîte arrondie
// calculée par pixel (le gradient de sa distance signée au point de la
// surface) : la courbure lisse d'un vrai arrondi pour le prix d'une boîte.
// Laque noire : une matcap générée (lib/matcap.js) noire à 97 %, avec deux
// boîtes à lumière placées là où la plongée à 55° renvoie les DESSUS et les
// ARÊTES (les faces avant, verticales, restent noires : c'est la laque). Face
// colorée mate, légèrement lumineuse. Pied assombri (ombre de contact).
// MSAA 4x dans une cible privée.
//
// LA CHUTE est entièrement dans le vertex shader : pour chaque parcours, la
// position normalisée s du domino le long du front est un attribut
// d'instance statique (calculé une fois) ; l'angle sort de l'horloge du cycle
// en uniforme. angle = 90° x easeInQuad, rebond de 5° amorti à l'arrivée.
//
// LE CYCLE (32 temps, 8 mesures, calé sur les mesures de la musique) :
// 4 mesures de chute, 2 couchés, 2 de relèvement en vague inverse. Un
// nouveau parcours à chaque cycle. Le brief demande aussi « une rangée par
// croche » : avec 4 mesures de chute ce n'est vrai que si le parcours fait
// 32 rangées (tier 0 en LINE) ; on garde le cycle calé sur la musique et le
// front avance, en continu, à la vitesse que demande son parcours (voir le
// test pour les vitesses mesurées).
//
// LA POCHETTE est échantillonnée dans un Web Worker au changement de morceau
// (R17) : carré central = la pochette entière (rows x rows), colonnes de côté
// = la couleur de bord prolongée et assombrie ; une pochette unie reçoit un
// léger dégradé de sa propre teinte. Livrée en texture de données, appliquée
// au cycle suivant (dominos debout : aucun saut de couleur visible).

const TAU = Math.PI * 2;
export const DIM = { p: 1, w: 0.86, h: 0.72, d: 0.18, m: 0.04, r: 0.07 };
export const CYCLE_BEATS = 32, FALL_BEATS = 16, LIE_BEATS = 8, RISE_BEATS = 8;
export const FALL_DUR = 0.5;            // beats: one domino's fall
const BOUNCE_DEG = 5;
const DROP_SPREAD = 0.5;                // beats: the radial explosion
const DROP_LIE = 1.5, DROP_RISE = 4;    // beats: lying, then up in one bar
const FLASH = 0.15;
export const PATHS = ['line', 'spiral', 'ring', 'diagonal', 'snake'];
const NO_BPM = 110;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const ELEVATION_DEG = 55, ORBIT_DEG = 10, ORBIT_BARS = 32;
const EDGE_MARGIN = 0.10;               // 5 % of the height
const EXCLUSIONS = [['tr', 0.22, 0.12], ['tl', 0.08, 0.12], ['br', 0.22, 0.10]];
const COLOUR_LUM_MAX = 0.62;            // the coloured faces stay under the bloom's threshold

const TIER = { 0: { cols: 32, rows: 20, msaa: true }, 1: { cols: 48, rows: 30, msaa: true }, 2: { cols: 64, rows: 40, msaa: true } };

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// --- the paths: s in [0, 1] for each domino, and the path's length in cells --
export function pathValues(path, cols, rows) {
  const n = cols * rows, s = new Float32Array(n);
  const cx = (cols - 1) / 2, cz = (rows - 1) / 2;
  let len = 1;
  if (path === 'line') {
    for (let z = 0; z < rows; z++) for (let x = 0; x < cols; x++) s[z * cols + x] = x;
    len = cols - 1;
  } else if (path === 'diagonal') {
    for (let z = 0; z < rows; z++) for (let x = 0; x < cols; x++) s[z * cols + x] = (x + (rows - 1 - z)) / Math.SQRT2;
    len = (cols - 1 + rows - 1) / Math.SQRT2;
  } else if (path === 'ring') {
    for (let z = 0; z < rows; z++) for (let x = 0; x < cols; x++) s[z * cols + x] = Math.hypot(x - cx, z - cz);
    len = Math.hypot(cx, cz);
  } else if (path === 'spiral') {
    // an Archimedean spiral from the centre, arms ARM cells apart; a
    // domino's place is the arc length up to the turn that passes it
    const ARM = 5, b = ARM / TAU;
    let mx = 0;
    for (let z = 0; z < rows; z++) for (let x = 0; x < cols; x++) {
      const dx = x - cx, dz = z - cz, r = Math.hypot(dx, dz);
      let th = Math.atan2(dz, dx); if (th < 0) th += TAU;
      const k = Math.max(0, Math.round((r / b - th) / TAU));
      const tt = th + TAU * k;
      const arc = 0.5 * b * (tt * Math.sqrt(1 + tt * tt) + Math.asinh(tt));
      s[z * cols + x] = arc; mx = Math.max(mx, arc);
    }
    len = mx;
  } else {
    // a snake through the whole grid: lanes of LANE rows, boustrophedon,
    // its head slanted across the lane
    const lanes = 5, lane = rows / lanes;
    for (let z = 0; z < rows; z++) for (let x = 0; x < cols; x++) {
      const L = Math.min(lanes - 1, Math.floor((rows - 1 - z) / lane));
      const inLane = (rows - 1 - z) - L * lane;
      const along = L % 2 === 0 ? x : cols - 1 - x;
      s[z * cols + x] = L * (cols + lane) + along + 0.6 * inLane;
    }
    len = lanes * (cols + lane);
  }
  let mx = 0;
  for (let i = 0; i < n; i++) mx = Math.max(mx, s[i]);
  for (let i = 0; i < n; i++) s[i] /= mx || 1;
  return { s, len };
}

// The angle (degrees) of a domino at `t` beats into its cycle, its own s:
// fall, bounce, lie, rise in the reverse order. JS copy of the GLSL.
export function cycleAngle(t, s) {
  const tf = s * (FALL_BEATS - FALL_DUR);
  const tr = FALL_BEATS + LIE_BEATS + (1 - s) * (RISE_BEATS - FALL_DUR);
  if (t >= tr) { const u = clamp01((t - tr) / FALL_DUR); return 90 * (1 - u) * (1 - u); }
  const u = clamp01((t - tf) / FALL_DUR);
  let a = 90 * u * u;
  if (u >= 1) { const k = (t - tf - FALL_DUR) / FALL_DUR; a = 90 - BOUNCE_DEG * Math.exp(-3 * k) * Math.abs(Math.sin(Math.PI * 2 * k)); }
  return a;
}
// The drop: radial explosion over half a beat from the centre, lie, rise in
// one bar from the outside in. `a0` = the angle when the drop began.
export function dropAngle(t, r, a0) {
  const tf = r * DROP_SPREAD;
  const u = clamp01((t - tf) / (FALL_DUR * 0.5));
  let a = Math.max(a0, 90 * u * u);
  if (u >= 1) { const k = (t - tf - FALL_DUR * 0.5) / FALL_DUR; a = 90 - BOUNCE_DEG * Math.exp(-3 * k) * Math.abs(Math.sin(Math.PI * 2 * Math.max(0, k))); }
  const tr = DROP_SPREAD + DROP_LIE + (1 - r) * (DROP_RISE - FALL_DUR);
  if (t >= tr) { const v = clamp01((t - tr) / FALL_DUR); a = 90 * (1 - v) * (1 - v); }
  return a;
}
export const DROP_BEATS = DROP_SPREAD + DROP_LIE + DROP_RISE + 0.5;

// --- the lacquer matcap: black, two wide soft boxes where the 55-degree view
// sends the TOPS (view normal y ~ +0.57) and the rounded EDGES; the vertical
// fronts (view y ~ -0.82) stay black ---------------------------------------
export function lacquerMatcap(size) {
  const n = size || 256, data = new Uint8Array(n * n * 4), px = 2 / n;
  const boxes = [
    { cx: -0.18, cy: 0.56, hw: 0.46, hh: 0.13, rr: 0.08 },   // the tops and the top edges
    { cx: 0.52, cy: 0.10, hw: 0.09, hh: 0.42, rr: 0.06 }     // a raking light on the right edges
  ];
  // the fronts (view normal y ~ -0.82): a DIM, wide reflection of a dark sky.
  // Without it a black front on the black floor vanished and a standing
  // domino read as a white dash (its lit top) instead of a lacquered slab.
  const sheen = [{ cx: 0.0, cy: -0.80, hw: 0.70, hh: 0.22, rr: 0.15 }];
  for (let j = 0; j < n; j++) {
    const y = ((j + 0.5) / n) * 2 - 1;
    for (let i = 0; i < n; i++) {
      const x = ((i + 0.5) / n) * 2 - 1, o = (j * n + i) * 4;
      const st = studio(x, y, px, { edgePx: 3, boxes });
      const inside = x * x + y * y <= 1 ? 1 : 0;
      const box = st.box;
      const sh = studio(x, y, px, { edgePx: 30, boxes: sheen }).box;
      const v = (MATCAP_DARK * 0.5 + 0.72 * box + 0.03 * sh * smooth01(-0.98, -0.6, y) + 0.07 * st.fres * smooth01(-0.2, 0.4, y)) * inside;
      const e = encoder(v);
      data[o] = e; data[o + 1] = e; data[o + 2] = Math.min(255, e + 2); data[o + 3] = 255;
    }
  }
  return { data, size: n };
}

// --- the cover mosaic: a worker samples the cover (R17) ---------------------
export function mosaicFromSquare(sq, rows, cols) {
  // sq: Uint8 RGBA rows x rows (the cover). The grid: the cover in the
  // centre, its edge colours prolonged and darkened on both sides.
  const out = new Uint8Array(cols * rows * 4);
  const x0 = Math.floor((cols - rows) / 2);
  let mean = [0, 0, 0], m2 = 0;
  for (let i = 0; i < rows * rows; i++) for (let c = 0; c < 3; c++) mean[c] += sq[i * 4 + c] / (rows * rows);
  for (let i = 0; i < rows * rows; i++) for (let c = 0; c < 3; c++) m2 += (sq[i * 4 + c] - mean[c]) ** 2 / (rows * rows * 3);
  const flat = Math.sqrt(m2) < 6;
  for (let z = 0; z < rows; z++) for (let x = 0; x < cols; x++) {
    const sx = Math.min(rows - 1, Math.max(0, x - x0));
    // texture row 0 is the row NEAREST the camera (the bottom of the screen):
    // the image's bottom row
    const o = ((rows - 1 - z) * cols + x) * 4, q = (z * rows + sx) * 4;
    const outside = x < x0 ? (x0 - x) / Math.max(1, x0) : (x >= x0 + rows ? (x - x0 - rows + 1) / Math.max(1, cols - x0 - rows) : 0);
    let k = outside > 0 ? 0.55 - 0.35 * outside : 1;
    if (flat) k *= 0.75 + 0.25 * Math.cos(Math.hypot(x - (cols - 1) / 2, z - (rows - 1) / 2) / rows * Math.PI);
    for (let c = 0; c < 3; c++) out[o + c] = Math.round(sq[q + c] * k);
    out[o + 3] = 255;
  }
  return { data: out, flat };
}
function mosaicWorkerSource() {
  return 'var mosaicFromSquare = ' + mosaicFromSquare.toString() + ';\n' +
    'self.onmessage = function (ev) {\n' +
    '  var m = ev.data, t0 = performance.now();\n' +
    '  var fail = function (e) { self.postMessage({ id: m.id, error: String(e && e.message || e) }); };\n' +
    '  if (typeof OffscreenCanvas === "undefined" || typeof createImageBitmap === "undefined") { fail("nooffscreen"); return; }\n' +
    '  fetch(m.url).then(function (r) { if (!r.ok) throw new Error("http " + r.status); return r.blob(); })\n' +
    '    .then(function (b) { return createImageBitmap(b); })\n' +
    '    .then(function (bmp) {\n' +
    '      var c = new OffscreenCanvas(m.rows, m.rows), g = c.getContext("2d");\n' +
    '      g.imageSmoothingQuality = "high";\n' +
    '      var s = Math.min(bmp.width, bmp.height);\n' +
    '      g.drawImage(bmp, (bmp.width - s) / 2, (bmp.height - s) / 2, s, s, 0, 0, m.rows, m.rows);\n' +
    '      if (bmp.close) bmp.close();\n' +
    '      var res = mosaicFromSquare(g.getImageData(0, 0, m.rows, m.rows).data, m.rows, m.cols);\n' +
    '      self.postMessage({ id: m.id, data: res.data, flat: res.flat, ms: performance.now() - t0 }, [res.data.buffer]);\n' +
    '    }).catch(fail);\n' +
    '};\n';
}
let _worker = null, _workerFailed = false, _nextId = 0;
const _waiting = new Map();
function askWorker(msg, cb) {
  if (!_worker && !_workerFailed) {
    try {
      if (typeof Worker === 'undefined') throw new Error('no worker');
      _worker = new Worker(URL.createObjectURL(new Blob([mosaicWorkerSource()], { type: 'text/javascript' })));
      _worker.onmessage = (ev) => { const f = _waiting.get(ev.data.id); if (f) { _waiting.delete(ev.data.id); f(ev.data); } };
      _worker.onerror = () => { for (const [id, f] of _waiting) f({ id, error: 'worker' }); _waiting.clear(); };
    } catch (e) { _workerFailed = true; _worker = null; }
  }
  if (!_worker) return false;
  _waiting.set(msg.id, cb);
  _worker.postMessage(msg);
  return true;
}
// No cover (or no worker): a radial gradient of the palette's colours.
export function fallbackMosaic(palette, cols, rows) {
  const src = palette && (palette.vividColors || palette.dominantColors) || [];
  const a = vdjColor(src[0] != null ? src[0] : 0x5a3cff, new THREE.Color()), b = vdjColor(src[1] != null ? src[1] : 0xff3c8c, new THREE.Color());
  const out = new Uint8Array(cols * rows * 4), c = new THREE.Color();
  for (let z = 0; z < rows; z++) for (let x = 0; x < cols; x++) {
    const r = Math.hypot((x - (cols - 1) / 2) / cols, (z - (rows - 1) / 2) / rows) * 2;
    c.copy(a).lerp(b, clamp01(r)).convertLinearToSRGB();
    const o = (z * cols + x) * 4;
    out[o] = c.r * 255; out[o + 1] = c.g * 255; out[o + 2] = c.b * 255; out[o + 3] = 255;
  }
  return out;
}

// --- the camera: the grid whole in the frame (R6) ---------------------------
function forbidden(nx, ny, aspect) {
  // nx, ny in NDC; the frame margin and the exclusion zones
  if (Math.abs(ny) > 1 - EDGE_MARGIN || Math.abs(nx) > 1 - EDGE_MARGIN / aspect) return true;
  for (const [corner, fw, fh] of EXCLUSIONS) {
    const x0 = corner[1] === 'r' ? 1 - 2 * fw : -1, x1 = corner[1] === 'r' ? 1 : -1 + 2 * fw;
    const y0 = corner[0] === 't' ? 1 - 2 * fh : -1, y1 = corner[0] === 't' ? 1 : -1 + 2 * fh;
    if (nx > x0 && nx < x1 && ny > y0 && ny < y1) return true;
  }
  return false;
}
export function cameraPose(dist, azDeg, target, out) {
  const el = ELEVATION_DEG * Math.PI / 180, az = azDeg * Math.PI / 180;
  const o = out || new THREE.Vector3();
  return o.set(target.x + Math.sin(az) * Math.cos(el) * dist, target.y + Math.sin(el) * dist, target.z - Math.cos(az) * Math.cos(el) * dist);
}
// The smallest distance that keeps the grid's box (and its standing height)
// inside the margins and out of the exclusion zones, at both orbit extremes.
// Returns { dist, tz }: the aim point slides along z too - aimed at the
// grid's centre, the grid sat low with the top third of the frame empty.
// Each aim point along z with its own closest distance; among those within
// 30 % of the closest overall, the one that CENTRES the grid vertically.
// The grid is wider than the frame once foreshortened, so the near edge's
// width sets the distance, and the closest aim left 40 % of the frame empty
// below the grid.
export function fitCamera(cols, rows, fovDeg, aspect) {
  const cands = [];
  for (let k = -12; k <= 12; k++) {
    const tz = k / 12 * rows * 0.4;
    cands.push({ tz, dist: fitDistance(cols, rows, fovDeg, aspect, tz) });
  }
  const dMin = Math.min(...cands.map((c) => c.dist));
  let best = null;
  for (const c of cands) {
    if (c.dist > dMin * 1.30) continue;
    const v = verticalSpan(cols, rows, fovDeg, aspect, c.tz, c.dist);
    const off = Math.abs(v.top + v.bottom);
    if (!best || off < best.off) best = { dist: c.dist, tz: c.tz, off };
  }
  return best;
}
function verticalSpan(cols, rows, fovDeg, aspect, tz, dist) {
  const cam = new THREE.PerspectiveCamera(fovDeg, aspect, 0.1, 1000), target = new THREE.Vector3(0, 0, tz), v = new THREE.Vector3();
  cameraPose(dist, 0, target, cam.position); cam.lookAt(target); cam.updateMatrixWorld(); cam.updateProjectionMatrix();
  let top = -9, bottom = 9;
  for (const x of [-cols / 2, cols / 2]) for (const z of [-rows / 2, rows / 2]) for (const y of [0, DIM.h]) {
    v.set(x, y, z).project(cam); top = Math.max(top, v.y); bottom = Math.min(bottom, v.y);
  }
  return { top, bottom };
}
export function fitDistance(cols, rows, fovDeg, aspect, tz) {
  const cam = new THREE.PerspectiveCamera(fovDeg, aspect, 0.1, 1000);
  const hx = cols / 2, hz = rows / 2, target = new THREE.Vector3(0, 0, tz || 0);
  const corners = [];
  for (const x of [-hx, hx]) for (const z of [-hz, hz]) for (const y of [0, DIM.h]) corners.push(new THREE.Vector3(x, y, z));
  // the hull's edges too, sampled: an exclusion zone can bite an edge between corners
  const edges = [];
  for (const y of [0, DIM.h]) for (let k = 0; k <= 16; k++) {
    const f = k / 16;
    edges.push(new THREE.Vector3(-hx + 2 * hx * f, y, -hz), new THREE.Vector3(-hx + 2 * hx * f, y, hz), new THREE.Vector3(-hx, y, -hz + 2 * hz * f), new THREE.Vector3(hx, y, -hz + 2 * hz * f));
  }
  const pts = corners.concat(edges), v = new THREE.Vector3();
  const ok = (dist) => {
    for (const az of [-ORBIT_DEG, 0, ORBIT_DEG]) {
      cameraPose(dist, az, target, cam.position); cam.lookAt(target); cam.updateMatrixWorld(); cam.updateProjectionMatrix();
      for (const p of pts) { v.copy(p).project(cam); if (forbidden(v.x, v.y, aspect)) return false; }
    }
    return true;
  };
  let lo = 5, hi = 400;
  for (let i = 0; i < 40; i++) { const m = (lo + hi) / 2; if (ok(m)) hi = m; else lo = m; }
  return hi;
}

// --- GLSL ----------------------------------------------------------------------
const dominoVertexShader = `
  precision highp float;
  attribute vec4 iGrid;      // x, z (world), u, v (mosaic)
  attribute vec4 iPathA;     // s for line, spiral, ring, diagonal
  attribute float iPathB;    // s for snake
  attribute float iDropR;    // radial distance, normalised (the drop)
  uniform float uPath;
  uniform float uCycleT;     // beats into the cycle
  uniform float uDrop;       // 1: the drop sequence
  uniform float uDropT;      // beats into the drop
  uniform float uDropCycleT; // the cycle's time when the drop began
  uniform float uBeat;       // whole beats into the cycle (flash)
  uniform float uBeatPhase;
  varying vec3 vLocal;       // the vertex in the domino's own frame (centred)
  varying float vAngle;      // radians
  varying vec2 vMosaic;
  varying float vFlash;
  varying vec3 vViewPos;
  const float H = ${DIM.h.toFixed(3)}, D = ${DIM.d.toFixed(3)}, P = ${DIM.p.toFixed(3)}, M = ${DIM.m.toFixed(3)};
  const float FALLB = ${FALL_BEATS.toFixed(1)}, LIEB = ${LIE_BEATS.toFixed(1)}, RISEB = ${RISE_BEATS.toFixed(1)}, DUR = ${FALL_DUR.toFixed(2)};

  float pathS() {
    if (uPath < 0.5) return iPathA.x;
    if (uPath < 1.5) return iPathA.y;
    if (uPath < 2.5) return iPathA.z;
    if (uPath < 3.5) return iPathA.w;
    return iPathB;
  }
  float bounce(float k) { return ${BOUNCE_DEG.toFixed(1)} * exp(-3.0 * k) * abs(sin(6.2831853 * k)); }
  // the cycle: fall, bounce, lie, rise (reverse order); returns degrees, and the landing time
  float cycleAngle(float t, float s, out float land) {
    float tf = s * (FALLB - DUR);
    land = tf + DUR;
    float tr = FALLB + LIEB + (1.0 - s) * (RISEB - DUR);
    if (t >= tr) { float u = clamp((t - tr) / DUR, 0.0, 1.0); return 90.0 * (1.0 - u) * (1.0 - u); }
    float u = clamp((t - tf) / DUR, 0.0, 1.0);
    if (u < 1.0) return 90.0 * u * u;
    return 90.0 - bounce((t - tf - DUR) / DUR);
  }

  void main() {
    float s = pathS();
    float land;
    float deg = cycleAngle(uCycleT, s, land);
    if (uDrop > 0.5) {
      float l2;
      float a0 = cycleAngle(uDropCycleT, s, l2);
      float tf = iDropR * ${DROP_SPREAD.toFixed(2)};
      float u = clamp((uDropT - tf) / (DUR * 0.5), 0.0, 1.0);
      deg = max(a0, 90.0 * u * u);
      if (u >= 1.0) deg = 90.0 - bounce(max(0.0, (uDropT - tf - DUR * 0.5) / DUR));
      float tr = ${DROP_SPREAD.toFixed(2)} + ${DROP_LIE.toFixed(2)} + (1.0 - iDropR) * (${DROP_RISE.toFixed(2)} - DUR);
      if (uDropT >= tr) { float v = clamp((uDropT - tr) / DUR, 0.0, 1.0); deg = 90.0 * (1.0 - v) * (1.0 - v); }
      land = 1e5;
    }
    float a = radians(deg);
    // the beat's click: the dominos that landed during the last beat
    vFlash = (land > uBeat - 1.0 && land <= uBeat) ? ${FLASH.toFixed(2)} * (1.0 - uBeatPhase) * (1.0 - uBeatPhase) : 0.0;

    // the box, base on the ground, then the fall about its front-bottom
    // edge (toward the camera, -z) at the BACK of its cell
    vec3 p = position;
    vLocal = p;
    vec3 q = vec3(p.x, p.y + H * 0.5, p.z + D * 0.5);          // pivot at the origin
    float c = cos(a), sn = sin(a);
    // toward the camera: +y goes to -z (at 90 degrees: y = q.z, z = -q.y)
    vec3 r = vec3(q.x, c * q.y + sn * q.z, -sn * q.y + c * q.z);
    float zPivot = P * 0.5 - M - D;
    vec3 world = vec3(iGrid.x + r.x, r.y, iGrid.y + zPivot + r.z);
    vAngle = a;
    vMosaic = iGrid.zw;
    vec4 mv = viewMatrix * vec4(world, 1.0);
    vViewPos = mv.xyz;
    gl_Position = projectionMatrix * mv;
  }
`;

const dominoFragmentShader = `
  precision highp float;
  uniform sampler2D uMatcap;
  uniform sampler2D uMosaic;
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  uniform float uShowNormals;
  varying vec3 vLocal;
  varying float vAngle;
  varying vec2 vMosaic;
  varying float vFlash;
  varying vec3 vViewPos;
  const vec3 HALF = vec3(${(DIM.w / 2).toFixed(3)}, ${(DIM.h / 2).toFixed(3)}, ${(DIM.d / 2).toFixed(3)});
  const float RAD = ${DIM.r.toFixed(3)};
  void main() {
    // the ROUNDED box's normal at this surface point: the gradient of its
    // signed distance (the point pulled onto the inner box, then outward)
    vec3 inner = HALF - RAD;
    vec3 e = vLocal - clamp(vLocal, -inner, inner);
    vec3 nl = length(e) > 1e-5 ? normalize(e) : vec3(0.0, 0.0, 1.0);
    // into the world: the fall's rotation about x
    float c = cos(vAngle), sn = sin(vAngle);
    vec3 nw = vec3(nl.x, c * nl.y + sn * nl.z, -sn * nl.y + c * nl.z);
    vec3 nv = normalize(mat3(viewMatrix) * nw);
    if (uShowNormals > 0.5) { gl_FragColor = vec4(nv * 0.5 + 0.5, 1.0); return; }
    vec2 muv = nv.xy * 0.49 + 0.5;
    vec3 mc = texture2D(uMatcap, muv).rgb;
    vec3 lacquer = mc * mc * ${MATCAP_GAIN.toFixed(2)};
    // the contact shadow: the foot of the black faces is darker
    float ao = mix(0.25, 1.0, smoothstep(-HALF.y, -HALF.y + 0.32, vLocal.y));
    lacquer *= ao;
    // the coloured face: the BACK one (local +z), up once fallen; matte,
    // faintly luminous, lit a little by the same studio from above
    float face = smoothstep(0.55, 0.92, nl.z);
    vec3 col = texture2D(uMosaic, vMosaic).rgb;
    float l = dot(col, vec3(0.2126, 0.7152, 0.0722));
    col *= min(1.0, ${COLOUR_LUM_MAX.toFixed(2)} / max(l, 1e-4));
    vec3 matte = col * (0.78 + 0.22 * clamp(nv.y, 0.0, 1.0)) * (1.0 + vFlash) + col * 0.06;
    vec3 rgb = mix(lacquer, matte, face);
    gl_FragColor = vec4(rgb, 1.0);
  }
`;

const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const displayFragmentShader = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb, 1.0); }
`;

export function dominoGeometry(cols, rows) {
  const box = new THREE.BoxGeometry(DIM.w, DIM.h, DIM.d);
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', box.getAttribute('position'));
  g.setIndex(box.getIndex());
  const n = cols * rows;
  const grid = new Float32Array(n * 4), pa = new Float32Array(n * 4), pb = new Float32Array(n), dr = new Float32Array(n);
  const paths = PATHS.map((p) => pathValues(p, cols, rows).s);
  const cx = (cols - 1) / 2, cz = (rows - 1) / 2, rmax = Math.hypot(cx, cz);
  for (let z = 0; z < rows; z++) for (let x = 0; x < cols; x++) {
    const i = z * cols + x;
    // world x runs RIGHT TO LEFT on screen (the camera looks along +z, so its
    // right is -x): column 0, the cover's left, sits at +x
    grid[i * 4] = (cx - x) * DIM.p; grid[i * 4 + 1] = (z - cz) * DIM.p;
    grid[i * 4 + 2] = (x + 0.5) / cols; grid[i * 4 + 3] = (z + 0.5) / rows;   // row 0 = nearest = bottom of the cover
    pa[i * 4] = paths[0][i]; pa[i * 4 + 1] = paths[1][i]; pa[i * 4 + 2] = paths[2][i]; pa[i * 4 + 3] = paths[3][i];
    pb[i] = paths[4][i];
    dr[i] = Math.hypot(x - cx, z - cz) / rmax;
  }
  g.setAttribute('iGrid', new THREE.InstancedBufferAttribute(grid, 4));
  g.setAttribute('iPathA', new THREE.InstancedBufferAttribute(pa, 4));
  g.setAttribute('iPathB', new THREE.InstancedBufferAttribute(pb, 1));
  g.setAttribute('iDropR', new THREE.InstancedBufferAttribute(dr, 1));
  g.instanceCount = n;
  box.dispose();
  return g;
}

export class DominoVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 1;
    this._L = TIER[this._tier];
    const t = parseFloat(urlParam('dominot'));
    this._forcePhase = Number.isFinite(t) ? clamp01(t) : null;
    const p = (urlParam('dominopath') || '').toLowerCase();
    this._forcePath = PATHS.includes(p) ? p : null;
    this._forceDrop = urlParam('dominodrop') === '1';
    this._showNormals = urlParam('dominonormals') === '1';
    this._post = { bloomStrength: 0.18, bloomThreshold: 0.85, grain: 0, vignette: 0.30, aberration: 0.0010, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
    if (urlParam('dominoprobe') === '1' && typeof window !== 'undefined') window.__domino = this;
  }

  get postProcessing() { return this._post; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const L = this._L;
    const mc = lacquerMatcap(256);
    this._matcap = new THREE.DataTexture(mc.data, mc.size, mc.size, THREE.RGBAFormat);
    this._matcap.minFilter = this._matcap.magFilter = THREE.LinearFilter; this._matcap.needsUpdate = true;
    this._mosaic = new THREE.DataTexture(fallbackMosaic(null, L.cols, L.rows), L.cols, L.rows, THREE.RGBAFormat);
    this._mosaic.colorSpace = THREE.SRGBColorSpace;
    this._mosaic.minFilter = this._mosaic.magFilter = THREE.NearestFilter; this._mosaic.needsUpdate = true;
    this._pendingMosaic = null;
    this._mosaicIsFallback = true;

    this._U = {
      uPath: { value: 0 }, uCycleT: { value: 0 }, uDrop: { value: 0 }, uDropT: { value: 0 }, uDropCycleT: { value: 0 },
      uBeat: { value: 0 }, uBeatPhase: { value: 0 },
      uMatcap: { value: this._matcap }, uMosaic: { value: this._mosaic }, uShowNormals: { value: this._showNormals ? 1 : 0 },
      uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._mat = new THREE.ShaderMaterial({ uniforms: this._U, vertexShader: dominoVertexShader, fragmentShader: dominoFragmentShader });
    this._mesh = new THREE.Mesh(dominoGeometry(L.cols, L.rows), this._mat);
    this._mesh.frustumCulled = false;

    // MSAA: the dominos in a private scene, rendered into a 4x target by the
    // display quad (the shared chain has no anti-aliasing)
    this._msaaTarget = null;
    this._private = new THREE.Scene();
    this._private.add(this._mesh);
    this._dispU = { uTex: { value: null } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._dispU, vertexShader: quadVertexShader, fragmentShader: displayFragmentShader, depthTest: false, depthWrite: false
    }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer, sc, cam) { self._renderDominos(renderer, cam); };
    scene.add(this._display);

    // state
    this._time = 0;
    this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null;
    this._bpm = NO_BPM;
    this._cycleStart = 0;
    this._pathIdx = this._forcePath ? PATHS.indexOf(this._forcePath) : 0;
    this._drop = null;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._artSeen = undefined; this._paletteSeen = undefined; this._mosaicJob = 0;
    this._fitKey = '';
    this._dist = 60;
    this._log = [];
  }

  // --- the cover --------------------------------------------------------------
  _requestMosaic(profile) {
    const url = profile.artworkUrl || null;
    // keyed on the url and the palette's COLOURS, not the palette object: a
    // rebuilt-but-equal palette must not relaunch the worker
    const pal = profile.artworkPalette;
    const palKey = pal ? String((pal.vividColors || []).slice(0, 2)) + '/' + String((pal.dominantColors || []).slice(0, 1)) : '';
    if (url === this._artSeen && palKey === this._paletteSeen) return;
    const first = this._artSeen === undefined;
    this._artSeen = url; this._paletteSeen = palKey;
    const L = this._L, id = ++_nextId, job = ++this._mosaicJob;
    const fallback = () => { this._pendingMosaic = { data: fallbackMosaic(profile.artworkPalette, L.cols, L.rows), first, fallback: true }; };
    if (!url) { fallback(); return; }
    const sent = askWorker({ id, url: artProxyUrl(url), rows: L.rows, cols: L.cols }, (m) => {
      if (job !== this._mosaicJob || !this._scene) return;
      if (m.error || !m.data) { fallback(); this._log.push({ what: 'mosaic', error: m.error }); return; }
      // the first real cover replaces a stand-in at once
      this._pendingMosaic = { data: new Uint8Array(m.data), first: first || this._mosaicIsFallback, flat: m.flat };
      this._log.push({ what: 'mosaic', ms: Math.round(m.ms), flat: m.flat });
    });
    if (!sent) fallback();
  }
  // a new mosaic goes in while the dominos stand (the start of a cycle), or
  // at once the first time
  _applyMosaic(force) {
    const P = this._pendingMosaic;
    if (!P || (!force && !P.first)) return;
    this._mosaic.image.data.set(P.data);
    this._mosaic.needsUpdate = true;
    this._mosaicIsFallback = !!P.fallback;
    this._pendingMosaic = null;
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  _newCycle(atBeat) {
    this._cycleStart = atBeat;
    if (!this._forcePath) this._pathIdx = (this._pathIdx + 1) % PATHS.length;
    this._applyMosaic(true);
    this._log.push({ what: 'cycle', beat: +atBeat.toFixed(2), path: PATHS[this._pathIdx] });
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._requestMosaic(profile);
    this._applyMosaic(false);
    if (profile.playbackState !== 'PLAYING') return;          // frozen
    this._time += dt;

    // the clock, in beats: integrated at the tempo, pulled toward the grid
    // with a whole number of bars of offset (its bars are the music's)
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? bpm : NO_BPM;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > 2) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else { const rate = this._bpm / 60; this._clock += Math.max(dt * rate * 0.5, dt * rate + err * Math.min(1, dt / 0.5)); }
    } else { this._clock += dt * this._bpm / 60; this._targetOffset = null; }

    // the drop: the analysis' marks, or ?dominodrop=1 (every 4 cycles' first downbeat + 2 bars)
    let drop = hasGrid && this._tickDrops(profile);
    if (this._forceDrop && Math.floor(this._clockPrev / 4) !== Math.floor(this._clock / 4) && Math.floor(this._clock / 4) % 8 === 6) drop = true;
    if (drop && !this._drop) {
      this._drop = { start: this._clock, cycleT: this._clock - this._cycleStart };
      this._log.push({ what: 'drop', beat: +this._clock.toFixed(2) });
    }
    let cycleT = this._clock - this._cycleStart;
    if (this._drop) {
      const dT = this._clock - this._drop.start;
      if (dT >= DROP_BEATS) {
        // up again: a new cycle on the next downbeat
        this._drop = null;
        this._newCycle(4 * Math.ceil(this._clock / 4));
        cycleT = this._clock - this._cycleStart;
      }
    }
    if (!this._drop && cycleT >= CYCLE_BEATS) { this._newCycle(this._cycleStart + CYCLE_BEATS * Math.floor(cycleT / CYCLE_BEATS)); cycleT = this._clock - this._cycleStart; }
    if (this._forcePhase != null) cycleT = this._forcePhase * CYCLE_BEATS;

    const U = this._U;
    U.uPath.value = this._pathIdx;
    U.uCycleT.value = Math.max(-1, cycleT);
    U.uDrop.value = this._drop ? 1 : 0;
    U.uDropT.value = this._drop ? this._clock - this._drop.start : 0;
    U.uDropCycleT.value = this._drop ? this._drop.cycleT : 0;
    U.uBeat.value = Math.floor(cycleT);
    U.uBeatPhase.value = cycleT - Math.floor(cycleT);

    // the camera: 55 degrees down, a slow orbit of +-10 degrees over 32 bars
    this._fitCamera();
    const az = ORBIT_DEG * Math.sin(TAU * this._clock / (4 * ORBIT_BARS));
    cameraPose(this._dist, az, _target, this._camera.position);
    this._camera.up.set(0, 1, 0);
    this._camera.lookAt(_target);
    this._camera.updateMatrixWorld();
  }

  _fitCamera() {
    const cam = this._camera;
    const key = [cam.fov, cam.aspect].map((v) => (+v).toFixed(3)).join(',');
    if (key === this._fitKey) return;
    this._fitKey = key;
    const f = fitCamera(this._L.cols, this._L.rows, cam.fov, cam.aspect);
    this._dist = f.dist; _target.set(0, 0, f.tz);
    if (cam.near > 1 || cam.far < this._dist * 2) { cam.near = Math.min(cam.near, 1); cam.far = Math.max(cam.far, this._dist * 3); cam.updateProjectionMatrix(); }
  }

  // The dominos into the private 4x MSAA target at the composer's size.
  _renderDominos(renderer, camera) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(_size); w = b.x; h = b.y; }
    if (!this._msaaTarget || this._msaaTarget.width !== w || this._msaaTarget.height !== h) {
      if (this._msaaTarget) this._msaaTarget.dispose();
      this._msaaTarget = new THREE.WebGLRenderTarget(w, h, { samples: this._L.msaa ? 4 : 0, depthBuffer: true, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    }
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    renderer.setRenderTarget(this._msaaTarget);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    renderer.render(this._private, camera);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
    this._dispU.uTex.value = this._msaaTarget.texture;
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
    for (const m of [this._display, this._mesh]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    if (this._msaaTarget) this._msaaTarget.dispose();
    if (this._matcap) this._matcap.dispose();
    if (this._mosaic) this._mosaic.dispose();
    if (typeof window !== 'undefined' && window.__domino === this) window.__domino = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
const _target = new THREE.Vector3(0, 0, 0);
